import type { Spell, Item, ClassFeature, Feat, FeatCategory } from '../types';

/**
 * Extracts the sheet ID from various Google Sheets URL formats.
 * Supports:
 * - https://docs.google.com/spreadsheets/d/SHEET_ID/edit
 * - https://docs.google.com/spreadsheets/d/SHEET_ID/
 * - https://docs.google.com/spreadsheets/d/SHEET_ID
 */
export function extractSheetId(url: string): string | null {
  const patterns = [
    /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/,
    /^([a-zA-Z0-9-_]+)$/, // Direct ID
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return match[1];
    }
  }

  return null;
}

/**
 * Extracts the gid (sheet tab ID) from a Google Sheets URL.
 * Returns undefined if not found.
 */
export function extractGid(url: string): number | undefined {
  // Match gid= in query string or hash
  const match = url.match(/[?&#]gid=(\d+)/);
  return match ? parseInt(match[1], 10) : undefined;
}

/**
 * Builds a CSV export URL from a Google Sheets ID.
 * If gid is omitted, Google exports the default/active sheet.
 */
export function buildCsvUrl(sheetId: string, gid?: number): string {
  const gidParam = gid !== undefined ? `&gid=${gid}` : '';
  return `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv${gidParam}`;
}

/**
 * Builds a GViz CSV URL from a Google Sheets ID and tab name.
 */
export function buildGvizCsvUrl(sheetId: string, tabName: string): string {
  return `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:csv&sheet=${encodeURIComponent(tabName)}`;
}

/**
 * Parses CSV content into an array of objects.
 * First row is treated as headers.
 * Handles quoted fields with embedded newlines and commas.
 */
export function parseCsv(csvText: string): Record<string, string>[] {
  const rows = parseCsvRows(csvText);
  if (rows.length < 2) return [];

  const headers = rows[0];
  const result: Record<string, string>[] = [];

  for (let i = 1; i < rows.length; i++) {
    const values = rows[i];
    // Skip empty rows (all empty strings)
    if (values.every(v => !v.trim())) continue;

    const row: Record<string, string> = {};
    headers.forEach((header, idx) => {
      row[header] = values[idx] || '';
    });
    result.push(row);
  }

  return result;
}

/**
 * Parses CSV text into a 2D array of strings.
 * Properly handles:
 * - Quoted fields containing commas
 * - Quoted fields containing newlines
 * - Escaped quotes ("" inside quoted fields)
 */
function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = '';
  let inQuotes = false;
  let i = 0;

  while (i < text.length) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        // Check for escaped quote
        if (text[i + 1] === '"') {
          currentField += '"';
          i += 2;
          continue;
        } else {
          // End of quoted field
          inQuotes = false;
          i++;
          continue;
        }
      } else {
        // Inside quotes, accept any character including newlines
        currentField += char;
        i++;
        continue;
      }
    }

    // Not in quotes
    if (char === '"') {
      // Start of quoted field
      inQuotes = true;
      i++;
    } else if (char === ',') {
      // Field separator
      currentRow.push(currentField.trim());
      currentField = '';
      i++;
    } else if (char === '\r') {
      // Handle \r\n or standalone \r
      if (text[i + 1] === '\n') {
        i++; // Skip the \r, the \n will be handled next
      } else {
        // Standalone \r is a row separator
        currentRow.push(currentField.trim());
        rows.push(currentRow);
        currentRow = [];
        currentField = '';
        i++;
      }
    } else if (char === '\n') {
      // Row separator
      currentRow.push(currentField.trim());
      rows.push(currentRow);
      currentRow = [];
      currentField = '';
      i++;
    } else {
      currentField += char;
      i++;
    }
  }

  // Don't forget the last field and row
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField.trim());
    rows.push(currentRow);
  }

  return rows;
}

// Monotonic counter so ids stay unique even when many entities are
// generated in the same millisecond (e.g. parsing one import batch).
let idSequence = 0;

/**
 * Generates a unique id for custom spells/items.
 */
function generateId(name: string, prefix: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return `${prefix}-${slug}-${Date.now().toString(36)}-${idSequence++}`;
}

/**
 * Converts a row from the spell template to our Spell format.
 * 
 * Template columns:
 * spell_name, level, range, components, duration, concentration,
 * casting_time, ritual, description, material, school, classes
 */
export function rowToSpell(row: Record<string, string>): Spell | null {
  const name = row['spell_name']?.trim();
  if (!name) return null;

  const levelStr = row['level']?.trim() || '0';
  const level = parseInt(levelStr, 10);

  // Parse concentration (can be "TRUE", "1", "Yes", etc.)
  const concStr = (row['concentration'] || '').toLowerCase().trim();
  const concentration = concStr === 'true' || concStr === '1' || concStr === 'yes' || concStr === '✓';

  // Parse ritual
  const ritualStr = (row['ritual'] || '').toLowerCase().trim();
  const ritual = ritualStr === 'true' || ritualStr === '1' || ritualStr === 'yes' || ritualStr === '✓';

  // Parse classes (comma-separated)
  const classesStr = row['classes'] || '';
  const classes = classesStr
    .split(',')
    .map(c => c.trim())
    .filter(c => c.length > 0);

  return {
    id: generateId(name, 'custom-spell'),
    name,
    level: isNaN(level) ? 0 : level,
    school: row['school']?.trim() || 'Unknown',
    castingTime: row['casting_time']?.trim() || '1 Action',
    range: row['range']?.trim() || 'Self',
    components: row['components']?.trim() || 'V',
    material: row['material']?.trim() || undefined,
    duration: row['duration']?.trim() || 'Instantaneous',
    concentration,
    ritual,
    description: row['description']?.trim() || '',
    classes,
    tags: [],
    source: 'Custom',
    srd: false,
  };
}

/**
 * Converts a row from the item template to our Item format.
 * 
 * Template columns:
 * item_name, type, description, rarity, weight, weapon_mastery,
 * damage, ac, range, attunement, max_charges, google_drive_image_url
 */
export function rowToItem(row: Record<string, string>): Item | null {
  const name = row['item_name']?.trim();
  if (!name) return null;

  // Parse attunement
  const attunementStr = (row['attunement'] || '').toLowerCase().trim();
  const attunement = attunementStr === 'true' || attunementStr === '1' || attunementStr === 'yes' || attunementStr === '✓';

  // Parse charges
  const chargesStr = row['max_charges']?.trim();
  const charges = chargesStr ? parseInt(chargesStr, 10) : undefined;

  const source = row['source']?.trim();

  return {
    id: generateId(name, 'custom-item'),
    name,
    type: row['type']?.trim() || 'Wondrous Item',
    rarity: row['rarity']?.trim() || 'Common',
    attunement,
    description: row['description']?.trim() || '',
    weight: row['weight']?.trim() || undefined,
    damage: row['damage']?.trim() || undefined,
    ac: row['ac']?.trim() || undefined,
    range: row['range']?.trim() || undefined,
    weaponMastery: row['weapon_mastery']?.trim() || undefined,
    charges: isNaN(charges ?? NaN) ? undefined : charges,
    srd: false,
    source: source || undefined,
  };
}

/**
 * Converts a row from the class feature template to our ClassFeature format.
 * 
 * Template columns:
 * feature_name, class_name, level, description, subclass, feature_type, source
 */
export function rowToClassFeature(row: Record<string, string>): ClassFeature | null {
  const name = row['feature_name']?.trim();
  if (!name) return null;

  const className = row['class_name']?.trim();
  if (!className) return null;

  const levelStr = row['level']?.trim() || '1';
  const level = parseInt(levelStr, 10);
  if (isNaN(level) || level < 1 || level > 20) return null;

  const featureTypeStr = row['feature_type']?.trim().toLowerCase();
  const featureType = featureTypeStr === 'invocation' ? 'invocation' : undefined;

  return {
    id: generateId(name, 'custom-feature'),
    name,
    className,
    subclass: row['subclass']?.trim() || undefined,
    featureType,
    level,
    description: row['description']?.trim() || '',
    source: row['source']?.trim() || 'Custom',
  };
}

export interface FetchSheetsResult<T> {
  data: T[];
  errors: string[];
}

/**
 * Generic function to fetch and parse entities from a Google Sheets URL.
 * 
 * @param url - The Google Sheets URL
 * @param rowParser - Function to convert a CSV row to the entity type
 * @param nameField - The column name used for the entity name (for error messages)
 */
async function fetchFromSheets<T>(
  url: string,
  rowParser: (row: Record<string, string>) => T | null,
  nameField: string,
): Promise<FetchSheetsResult<T>> {
  const sheetId = extractSheetId(url);
  if (!sheetId) {
    return { data: [], errors: ['Invalid Google Sheets URL'] };
  }

  const gid = extractGid(url);
  const csvUrl = buildCsvUrl(sheetId, gid);
  
  try {
    const response = await fetch(csvUrl);
    if (!response.ok) {
      return { 
        data: [], 
        errors: [`Failed to fetch sheet: ${response.statusText}. Make sure the sheet is shared publicly.`] 
      };
    }

    const csvText = await response.text();
    const rows = parseCsv(csvText);
    
    const items: T[] = [];
    const errors: string[] = [];

    rows.forEach((row, idx) => {
      const item = rowParser(row);
      if (item) {
        items.push(item);
      } else if (row[nameField]?.trim()) {
        errors.push(`Row ${idx + 2}: Failed to parse "${row[nameField]}"`);
      }
    });

    return { data: items, errors };
  } catch (error) {
    return { 
      data: [], 
      errors: [`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`] 
    };
  }
}

/**
 * Fetches and parses spells from a Google Sheets URL.
 */
export async function fetchSpellsFromSheets(url: string): Promise<FetchSheetsResult<Spell>> {
  return fetchFromSheets(url, rowToSpell, 'spell_name');
}

/**
 * Fetches and parses items from a Google Sheets URL.
 */
export async function fetchItemsFromSheets(url: string): Promise<FetchSheetsResult<Item>> {
  return fetchFromSheets(url, rowToItem, 'item_name');
}

/**
 * Fetches and parses class features from a Google Sheets URL.
 */
export async function fetchClassesFromSheets(url: string): Promise<FetchSheetsResult<ClassFeature>> {
  return fetchFromSheets(url, rowToClassFeature, 'feature_name');
}

const VALID_FEAT_CATEGORIES = new Set<string>([
  'Origin',
  'General',
  'Fighting Style',
  'Epic Boon',
  'Dragonmark',
  'Greater Dragonmark',
]);

/**
 * Converts a row from the feat template to our Feat format.
 *
 * Template columns:
 * feat_name, category, prerequisite, description, source
 */
export function rowToFeat(row: Record<string, string>): Feat | null {
  const name = row['feat_name']?.trim();
  if (!name) return null;

  const categoryRaw = row['category']?.trim();
  const category: FeatCategory = VALID_FEAT_CATEGORIES.has(categoryRaw)
    ? (categoryRaw as FeatCategory)
    : 'General';

  return {
    id: generateId(name, 'custom-feat'),
    name,
    category,
    prerequisite: row['prerequisite']?.trim() || undefined,
    description: row['description']?.trim() || '',
    source: row['source']?.trim() || 'Custom',
  };
}

/**
 * Fetches and parses feats from a Google Sheets URL.
 */
export async function fetchFeatsFromSheets(url: string): Promise<FetchSheetsResult<Feat>> {
  return fetchFromSheets(url, rowToFeat, 'feat_name');
}

function parseRowsByType<T>(
  rows: Record<string, string>[],
  rowParser: (row: Record<string, string>) => T | null,
  nameField: string,
): FetchSheetsResult<T> {
  const data: T[] = [];
  const errors: string[] = [];

  rows.forEach((row, idx) => {
    const item = rowParser(row);
    if (item) {
      data.push(item);
    } else if (row[nameField]?.trim()) {
      errors.push(`Row ${idx + 2}: Failed to parse "${row[nameField]}"`);
    }
  });

  return { data, errors };
}

async function fetchRowsFromTab(sheetId: string, tabName: string): Promise<Record<string, string>[] | null> {
  const csvUrl = buildGvizCsvUrl(sheetId, tabName);
  const response = await fetch(csvUrl);

  if (!response.ok) {
    return null;
  }

  const csvText = await response.text();
  const trimmed = csvText.trim().toLowerCase();

  if (trimmed.startsWith('<!doctype html') || trimmed.startsWith('<html')) {
    return null;
  }

  return parseCsv(csvText);
}

interface MultiTabTypeResult<T> {
  data: T[];
  errors: string[];
  importedTab: string | null;
}

async function fetchKnownTabType<T>(
  sheetId: string,
  expectedType: 'spells' | 'items' | 'classes' | 'feats',
  tabNames: string[],
  rowParser: (row: Record<string, string>) => T | null,
  nameField: string,
): Promise<MultiTabTypeResult<T>> {
  for (const tabName of tabNames) {
    try {
      const rows = await fetchRowsFromTab(sheetId, tabName);
      if (!rows || rows.length === 0) {
        continue;
      }

      const headers = Object.keys(rows[0]);
      const detectedType = detectSheetType(headers);
      if (detectedType !== expectedType) {
        continue;
      }

      const parsed = parseRowsByType(rows, rowParser, nameField);
      return {
        data: parsed.data,
        errors: parsed.errors,
        importedTab: tabName,
      };
    } catch {
      continue;
    }
  }

  return { data: [], errors: [], importedTab: null };
}

export interface MultiTabImportResult {
  data: {
    spells: Spell[];
    items: Item[];
    classes: ClassFeature[];
    feats: Feat[];
  };
  errorsByType: {
    spells: string[];
    items: string[];
    classes: string[];
    feats: string[];
  };
  importedTabs: string[];
  errors: string[];
}

/**
 * Fetches known tab types from a single multi-tab Google Sheet.
 */
export async function fetchFromSheetsMultiTab(url: string): Promise<MultiTabImportResult> {
  const sheetId = extractSheetId(url);
  if (!sheetId) {
    return {
      data: { spells: [], items: [], classes: [], feats: [] },
      errorsByType: { spells: [], items: [], classes: [], feats: [] },
      importedTabs: [],
      errors: ['Invalid Google Sheets URL'],
    };
  }

  const [spellsResult, itemsResult, classesResult, featsResult] = await Promise.all([
    fetchKnownTabType(sheetId, 'spells', ['Spells', 'Spell'], rowToSpell, 'spell_name'),
    fetchKnownTabType(sheetId, 'items', ['Items', 'Item'], rowToItem, 'item_name'),
    fetchKnownTabType(sheetId, 'classes', ['Class Features', 'Class Feature', 'Classes'], rowToClassFeature, 'feature_name'),
    fetchKnownTabType(sheetId, 'feats', ['Feats', 'Feat'], rowToFeat, 'feat_name'),
  ]);

  const importedTabs = [
    spellsResult.importedTab,
    itemsResult.importedTab,
    classesResult.importedTab,
    featsResult.importedTab,
  ].filter((tab): tab is string => tab !== null);

  if (importedTabs.length === 0) {
    return {
      data: { spells: [], items: [], classes: [], feats: [] },
      errorsByType: { spells: [], items: [], classes: [], feats: [] },
      importedTabs: [],
      errors: ['No supported tabs found. Expected one or more tabs named Spells, Items, Class Features, or Feats.'],
    };
  }

  return {
    data: {
      spells: spellsResult.data,
      items: itemsResult.data,
      classes: classesResult.data,
      feats: featsResult.data,
    },
    errorsByType: {
      spells: spellsResult.errors,
      items: itemsResult.errors,
      classes: classesResult.errors,
      feats: featsResult.errors,
    },
    importedTabs,
    errors: [],
  };
}

/**
 * Determines the entity type based on CSV headers.
 * Returns 'spells', 'items', 'classes', 'feats', or null if unrecognized.
 */
export function detectSheetType(headers: string[]): 'spells' | 'items' | 'classes' | 'feats' | null {
  const normalizedHeaders = headers.map(h => h.toLowerCase().trim());

  // Spell headers: spell_name, level, school, casting_time, etc.
  if (normalizedHeaders.includes('spell_name')) {
    return 'spells';
  }

  // Item headers: item_name, type, rarity, etc.
  if (normalizedHeaders.includes('item_name')) {
    return 'items';
  }

  // Class feature headers: feature_name, class_name, level
  if (normalizedHeaders.includes('feature_name') && normalizedHeaders.includes('class_name')) {
    return 'classes';
  }

  // Feat headers: feat_name, category
  if (normalizedHeaders.includes('feat_name')) {
    return 'feats';
  }

  return null;
}

export type UnifiedImportResult =
  | { type: 'spells'; data: Spell[]; errors: string[] }
  | { type: 'items'; data: Item[]; errors: string[] }
  | { type: 'classes'; data: ClassFeature[]; errors: string[] }
  | { type: 'feats'; data: Feat[]; errors: string[] }
  | { type: null; data: []; errors: string[] };

/**
 * Fetches and parses entities from a Google Sheets URL, auto-detecting the type.
 */
export async function fetchFromSheetsUnified(url: string): Promise<UnifiedImportResult> {
  const sheetId = extractSheetId(url);
  if (!sheetId) {
    return { type: null, data: [], errors: ['Invalid Google Sheets URL'] };
  }

  const gid = extractGid(url);
  const csvUrl = buildCsvUrl(sheetId, gid);
  
  try {
    const response = await fetch(csvUrl);
    if (!response.ok) {
      return { 
        type: null,
        data: [], 
        errors: [`Failed to fetch sheet: ${response.statusText}. Make sure the sheet is shared publicly.`] 
      };
    }

    const csvText = await response.text();
    const rows = parseCsv(csvText);
    
    if (rows.length === 0) {
      return { type: null, data: [], errors: ['Sheet is empty or has no data rows'] };
    }

    // Get headers from the first parsed row
    const headers = Object.keys(rows[0]);
    const detectedType = detectSheetType(headers);
    
    if (detectedType === null) {
      return {
        type: null,
        data: [],
        errors: ['Could not detect sheet type. Make sure your sheet uses the correct column names (spell_name, item_name, feature_name, or feat_name).'],
      };
    }

    // Parse based on detected type
    if (detectedType === 'spells') {
      const items: Spell[] = [];
      const errors: string[] = [];
      
      rows.forEach((row, idx) => {
        const item = rowToSpell(row);
        if (item) {
          items.push(item);
        } else if (row['spell_name']?.trim()) {
          errors.push(`Row ${idx + 2}: Failed to parse "${row['spell_name']}"`);
        }
      });
      
      return { type: 'spells', data: items, errors };
    }
    
    if (detectedType === 'items') {
      const items: Item[] = [];
      const errors: string[] = [];
      
      rows.forEach((row, idx) => {
        const item = rowToItem(row);
        if (item) {
          items.push(item);
        } else if (row['item_name']?.trim()) {
          errors.push(`Row ${idx + 2}: Failed to parse "${row['item_name']}"`);
        }
      });
      
      return { type: 'items', data: items, errors };
    }
    
    if (detectedType === 'classes') {
      const items: ClassFeature[] = [];
      const errors: string[] = [];

      rows.forEach((row, idx) => {
        const item = rowToClassFeature(row);
        if (item) {
          items.push(item);
        } else if (row['feature_name']?.trim()) {
          errors.push(`Row ${idx + 2}: Failed to parse "${row['feature_name']}"`);
        }
      });

      return { type: 'classes', data: items, errors };
    }

    // detectedType === 'feats'
    const items: Feat[] = [];
    const errors: string[] = [];

    rows.forEach((row, idx) => {
      const item = rowToFeat(row);
      if (item) {
        items.push(item);
      } else if (row['feat_name']?.trim()) {
        errors.push(`Row ${idx + 2}: Failed to parse "${row['feat_name']}"`);
      }
    });

    return { type: 'feats', data: items, errors };
  } catch (error) {
    return { 
      type: null,
      data: [], 
      errors: [`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`] 
    };
  }
}

export interface PredefinedSource {
  name: string;
  url: string;
}

export const PREDEFINED_SOURCES: PredefinedSource[] = [
  { name: "Acquisitions, Inc", url: "https://docs.google.com/spreadsheets/d/1WGQIoAqWDos2AnV5182c_cnH6KDLRBV_uQsFHruvM1M/edit?usp=drive_link" },
  { name: "Astarion's Book of Hungers", url: "https://docs.google.com/spreadsheets/d/1Tz4F5hAEtOfNSjOkuJcMn69QVEgiopwKu8m83O009BM/edit?usp=sharing" },
  { name: "Bigby Presents: Glory of the Giants", url: "https://docs.google.com/spreadsheets/d/1hkBM32p3JC5StM1KRuHGjrrGFfoWbdmJZfjljTyX7kY/edit?usp=sharing" },
  { name: "Dragonlance: Shadow of the Dragon Queen", url: "https://docs.google.com/spreadsheets/d/19d9eupdKFuPPuQ9b13FQ8LMPmp782JN9MmNYrzsAmYM/edit?usp=sharing" },
  { name: "Dungeon Masters Guide 2014", url: "https://docs.google.com/spreadsheets/d/1U6Po8I50NNt5UO4XZk99TZBo8wbevpQNDW_UpdA2Gh4/edit?usp=sharing" },
  { name: "Eberron: Forge of the Artificer", url: "https://docs.google.com/spreadsheets/d/1IyF3xddbK_tCbBxXqZsIPe5eTkoJ1vH9UdLGy4KTUCE/edit?usp=sharing" },
  { name: "Eberron: Rising from the Last War", url: "https://docs.google.com/spreadsheets/d/1OTCFNupGYLLaD8caKsjkzjxWISED6sM87tZboOTmz_o/edit?usp=sharing" },
  { name: "Elemental Evil Player's Companion", url: "https://docs.google.com/spreadsheets/d/1Nd5gOmW-hUQAp8wWuHAil7s33FYsRvEDMOQoFpmDwl0/edit?usp=sharing" },
  { name: "Explorer's Guide to Wildemount", url: "https://docs.google.com/spreadsheets/d/1tCrROo3NYX3g61pg4fT2W1IuQetLMRNtQeIVYuBJQgo/edit?usp=sharing" },
  { name: "Fizban's Treasury of Dragons", url: "https://docs.google.com/spreadsheets/d/1TrPAjOJCtLLSzNiskdfKHB9PlZqHTLHa6xkkv2HAtsQ/edit?usp=sharing" },
  { name: "Forgotten Realms: Heroes of Faerun", url: "https://docs.google.com/spreadsheets/d/1eN_sDreyfWKxvVbjzTzpNPQT7O7jk5AWJQOjeor8xOk/edit?usp=sharing" },
  { name: "Guildmasters Guide to Ravnica", url: "https://docs.google.com/spreadsheets/d/1MWoGwcVhg9Mb6-JaEinitnstB68xwdSxIeaBh4pcli0/edit?usp=sharing" },
  { name: "Icewind Dale: Rime of the Frostmaiden", url: "https://docs.google.com/spreadsheets/d/1H9Q9XperOs0CNl4cL8xe7N5G2zfpOQwJUjbz6jVN0oo/edit?usp=sharing" },
  { name: "Lorwyn: First Light", url: "https://docs.google.com/spreadsheets/d/184Iyht14u7Koto-WLallhduw7O1-FojxigVykagmKzY/edit?usp=sharing" },
  { name: "Lost Laboratory of Kwalish", url: "https://docs.google.com/spreadsheets/d/1c10gIlJBKpiG8AjypXQFJHenvthAxOwomX94iLEnejQ/edit?usp=sharing" },
  { name: "Mythic Odysseys of Theros", url: "https://docs.google.com/spreadsheets/d/1nrhyYw2KyoIAg0jSXYSN9hcPEyT07a91pKtAj3Spju4/edit?usp=sharing" },
  { name: "Planeshift: Amonkhet", url: "https://docs.google.com/spreadsheets/d/1FMwlU1d-oKP93vhdeF-y4Q2yKBYHP1AjKN-wfY72giI/edit?usp=sharing" },
  { name: "Planeshift: Kaladesh", url: "https://docs.google.com/spreadsheets/d/1QZNrnCy_uOl79KO11tYI6Qrpm9dzN4bgnOMQJHS1kDI/edit?usp=sharing" },
  { name: "Planescape: Adventures in the Multiverse", url: "https://docs.google.com/spreadsheets/d/17qzGc6z9MRrDSvP9xNOspzqA3KWXOHBYGNLH9HE-V74/edit?usp=sharing" },
  { name: "Players Handbook (2014)", url: "https://docs.google.com/spreadsheets/d/1BfHQ1qpD3Xznr_NZQ7lBtujbE5rX9WSc1N9wocr7A6k/edit?usp=sharing" },
  { name: "Players Handbook (2024)", url: "https://docs.google.com/spreadsheets/d/13CVh-krXbF2t0N2Y1K0a1I8ZO3yCeZqWWg-UhCPK5vM/edit?usp=sharing" },
  { name: "Spelljammer: Adventures in Space - Astral Adventurer's Guide", url: "https://docs.google.com/spreadsheets/d/1LNRDScMTVpba0LkhCmTX_y5bow2k7tu5yIEgkaGvChE/edit?usp=sharing" },
  { name: "Strixhaven: A Curriculum of Chaos", url: "https://docs.google.com/spreadsheets/d/18zvWaZMTOZBt1qoGPR7bvdH4hDhOhlRyNspWRYrZkZY/edit?usp=sharing" },
  { name: "Sword Coast Adventurer's Guide", url: "https://docs.google.com/spreadsheets/d/1czkW6jaWRygjO5teAgPESulXYirvR-i7msHDZM7-2XE/edit?usp=sharing" },
  { name: "Taldorei's Campaign Setting Reborn", url: "https://docs.google.com/spreadsheets/d/1fyLOv5iNqR5R1TcqFiZtSSspxp3Vc0o5h0mxNwGla8k/edit?usp=sharing" },
  { name: "Tasha's Cauldron of Everything", url: "https://docs.google.com/spreadsheets/d/1BRNhkOQnbvHmksKL6y3K7ArwseBzwMD6LPvdMl-LsIQ/edit?usp=sharing" },
  { name: "The Book of Many Things", url: "https://docs.google.com/spreadsheets/d/1x1q8kvlV6YIKmeZ77j0CVLX3UnJIUP7VS6Yk-HZLqoY/edit?usp=sharing" },
  { name: "Van Richten's Guide to Ravenloft", url: "https://docs.google.com/spreadsheets/d/1hY_YFcjj0lYkRg0kH11gnkluEsP80qYhWXie5L_nFe8/edit?usp=sharing" },
  { name: "Xanathar's Guide to Everything", url: "https://docs.google.com/spreadsheets/d/1jclXa1EVcMwc5FyCqA7K3Ni7NLpfsQyf2pm0rMt8edk/edit?usp=sharing" },
];

// Template URLs for reference
export const COMBINED_TEMPLATE_URL = 'https://docs.google.com/spreadsheets/d/17O2OGIU4aOV90y0j9jswbpyrclFERC8t1qVbm-xoboI/edit?usp=drive_link';
export const SPELL_TEMPLATE_URL = COMBINED_TEMPLATE_URL;
export const ITEM_TEMPLATE_URL = 'https://docs.google.com/spreadsheets/d/1oBGMIrEvqqHTKkYt-ecGDSbiJhXW8f0uDU9evNc-vpA/edit?usp=drive_link';
export const CLASS_TEMPLATE_URL = 'https://docs.google.com/spreadsheets/d/1BpT2RULDsa41LjDm2ezJQGLSPaK2yb0oNzq0FBdX0bI/edit?usp=sharing';
// Feat template columns: feat_name, category, prerequisite, description, source
export const FEAT_TEMPLATE_URL = 'https://docs.google.com/spreadsheets/d/1_w1xKYuh7V9C-CS5Cb2aVo8ZwuzDCv9EJC1_20OXALY/edit?usp=sharing';
